/**
 * Legacy Chain Engine
 * Builds a unified cross-memorial force-graph dataset by merging all family
 * trees and detecting inter-memorial surname / location / era connections.
 */
import { DEFAULT_FAMILY_TREES } from '../components/Memorial/FamilyTree/familyTreeData';
import { mockMemorials } from '../data/mockData';

const MEMORIAL_COLORS = {
  m1: { primary: '#1E3A5F', light: '#E8F0FA', accent: '#C9A84C' },
  m2: { primary: '#7C3AED', light: '#F3EEFF', accent: '#A78BFA' },
  m3: { primary: '#059669', light: '#ECFDF5', accent: '#34D399' },
  'my-network': { primary: '#0F766E', light: '#F0FDFA', accent: '#2DD4BF' },
};
const DEFAULT_COLOR = { primary: '#64748B', light: '#F1F5F9', accent: '#94A3B8' };

export function getMemorialColor(memId) {
  return MEMORIAL_COLORS[memId] || DEFAULT_COLOR;
}

export function buildLegacyChainGraph(familyMembers = []) {
  const nodes = [];
  const edges = [];
  const memorialClusters = [];
  const nodeIndex = new Map();

  // ── 1. Build nodes per memorial ──────────────────────────────────────────
  Object.entries(DEFAULT_FAMILY_TREES).forEach(([memId, tree]) => {
    const memorial = mockMemorials.find(m => m.id === memId);
    const color = getMemorialColor(memId);
    const clusterNodes = [];

    tree.nodes.forEach(node => {
      const id = `${memId}:${node.id}`;
      const COUSIN_RELATIONS = ['Cousin', 'Cousin (once removed)', 'Cousin (twice removed)'];
      const isCousin = COUSIN_RELATIONS.includes(node.relation);

      const graphNode = {
        id,
        memorialId: memId,
        memorialName: memorial?.name || memId,
        name: node.name,
        relation: node.relation,
        isCousin,
        birthYear: node.birthYear,
        deathYear: node.deathYear,
        isDeceased: node.isDeceased,
        generation: node.generation ?? 0,
        avatar: node.avatar || null,
        bio: node.bio || '',
        color: isCousin ? '#DB2777' : color.primary,
        lightColor: isCousin ? '#FDF2F8' : color.light,
        accentColor: isCousin ? '#DB2777' : color.accent,
        type: 'person',
      };
      nodes.push(graphNode);
      nodeIndex.set(id, graphNode);
      clusterNodes.push(id);
    });

    tree.links.forEach(link => {
      const isCousin = link.type === 'cousin';
      edges.push({
        id: `e-${memId}-${link.source}-${link.target}`,
        source: `${memId}:${link.source}`,
        target: `${memId}:${link.target}`,
        type: link.type,
        crossMemorial: false,
        memorialId: memId,
        color: isCousin ? '#DB2777' : color.primary,
      });
    });

    memorialClusters.push({
      id: memId,
      name: memorial?.name || memId,
      color: color.primary,
      lightColor: color.light,
      accentColor: color.accent,
      nodeIds: clusterNodes,
      profileImage: memorial?.profileImage,
      location: memorial?.location,
      category: memorial?.category,
    });
  });

  // ── 2. Add saved Family Network members when the user syncs ─────────────
  if (familyMembers.length > 0) {
    const memId = 'my-network';
    const color = getMemorialColor(memId);
    const clusterNodes = [];

    familyMembers.forEach((member, index) => {
      const id = `${memId}:${index}`;
      const isDeceased = (member?.is_deceased ?? 0) === 1;
      const graphNode = {
        id,
        memorialId: memId,
        memorialName: 'My Family Network',
        name: member?.full_name ?? '',
        relation: member?.relationship ?? 'Family member',
        isCousin: (member?.relationship ?? '').toLowerCase().includes('cousin'),
        birthYear: member?.birth_year ?? null,
        deathYear: member?.death_year ?? null,
        isDeceased,
        generation: 0,
        avatar: member?.avatar_url ?? null,
        bio: member?.bio ?? '',
        color: color.primary,
        lightColor: color.light,
        accentColor: color.accent,
        type: 'person',
      };
      nodes.push(graphNode);
      nodeIndex.set(id, graphNode);
      clusterNodes.push(id);
    });

    memorialClusters.push({
      id: memId,
      name: 'My Family Network',
      color: color.primary,
      lightColor: color.light,
      accentColor: color.accent,
      nodeIds: clusterNodes,
    });
  }

  // ── 3. Cross-memorial bridges for shared surnames and similar generations ─
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const a = nodes[i];
      const b = nodes[j];
      if (a.memorialId === b.memorialId) continue;

      const lastA = (a.name ?? '').trim().split(/\s+/).pop()?.toLowerCase() ?? '';
      const lastB = (b.name ?? '').trim().split(/\s+/).pop()?.toLowerCase() ?? '';
      const sameGeneration = Math.abs((a.generation ?? 0) - (b.generation ?? 0)) <= 1;

      if (lastA === lastB && lastA.length > 3 && sameGeneration) {
        edges.push({
          id: `bridge-${a.id}-${b.id}`,
          source: a.id,
          target: b.id,
          type: 'cross_surname',
          crossMemorial: true,
          color: '#C9A84C',
          label: `Shared surname "${lastA}"`,
        });
      }
    }
  }

  return { nodes, edges, memorialClusters };
}

// ── Layout: Circular cluster layout ─────────────────────────────────────────
export function computeLayout(nodes, memorialClusters, width, height) {
  const cx = width / 2;
  const cy = height / 2;
  const clusterRadius = Math.min(width, height) * 0.32;
  const nodeRadius = Math.min(width, height) * 0.12;
  const positions = {};

  memorialClusters.forEach((cluster, ci) => {
    const angle = (ci / memorialClusters.length) * Math.PI * 2 - Math.PI / 2;
    const clusterCx = cx + Math.cos(angle) * clusterRadius;
    const clusterCy = cy + Math.sin(angle) * clusterRadius;

    const clusterNodes = nodes.filter(n => cluster.nodeIds.includes(n.id));
    clusterNodes.forEach((node, ni) => {
      const nAngle = (ni / clusterNodes.length) * Math.PI * 2;
      const r = nodeRadius * (0.5 + Math.random() * 0.5);
      positions[node.id] = {
        x: clusterCx + Math.cos(nAngle) * r,
        y: clusterCy + Math.sin(nAngle) * r,
        cx: clusterCx,
        cy: clusterCy,
      };
    });
  });

  return positions;
}